/**
 * 状态哈希 — 对 WorldState 做规范化（对象键排序）后取 FNV-1a。
 * 仅依赖可序列化数据，排除纯展示字段；同种子同命令序列必得同哈希。
 *
 * 性能：空间网格（1920 cell）是全状态最大的部分。占用变化的频率远低于 Tick 数，
 * 因此对 spatial 子状态做内容缓存——仅在序列化内容变化时重算其哈希。
 */

import type { SpatialMapData } from './spatial/types.js';
import type { WorldState } from './world-state.js';

function sortValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortValue);
  if (typeof value === 'object' && value !== null) {
    const src = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(src).sort()) out[key] = sortValue(src[key]);
    return out;
  }
  return value;
}

export function canonicalize(value: unknown): string {
  return JSON.stringify(sortValue(value));
}

export function fnv1a32(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/**
 * spatial 哈希 = 静态部分（结构/属性，几乎不变）+ 动态部分（占用/危险等级）。
 * 静态部分以 config 为键缓存（同一张地图静态属性不变），避免每个 Tick 重建/重算整张 1920-cell 地图。
 * 动态部分只序列化会变字段，体量小、每 Tick 开销可控。
 */
const staticHashByConfig = new Map<string, string>();

/** cell 的动态字段（会随游戏变化）。 */
function dynamicCellSlice(c: SpatialMapData['cells'][string]): unknown {
  return [c.occupiedBy, c.hazardLevel, c.visible, c.buildable];
}

/** 仅哈希 spatial 地图：静态结构按 config 缓存 + 动态字段增量。 */
export function hashSpatial(map: SpatialMapData): { hash: string } {
  const configKey = JSON.stringify(map.config);
  let staticHash = staticHashByConfig.get(configKey);
  const keys = Object.keys(map.cells).sort();
  if (staticHash === undefined) {
    // 首次：计算静态结构哈希（坐标 + 静态属性）
    const staticParts: unknown[] = [map.config];
    for (const k of keys) {
      const c = map.cells[k];
      if (!c) continue;
      staticParts.push([
        c.layer, c.ring, c.sector, c.slopeBasisPoints, c.support, c.fracture,
        c.contamination, c.temperature, c.waterContent, c.district, c.excavatable,
      ]);
    }
    staticHash = fnv1a32(JSON.stringify(staticParts));
    if (staticHashByConfig.size >= 8) staticHashByConfig.clear();
    staticHashByConfig.set(configKey, staticHash);
  }
  // 动态：仅会变字段
  const dynamicParts: unknown[] = [];
  for (const k of keys) {
    const c = map.cells[k];
    if (c) dynamicParts.push(dynamicCellSlice(c));
  }
  const dynamicHash = fnv1a32(JSON.stringify(dynamicParts));
  return { hash: fnv1a32(staticHash + ':' + dynamicHash) };
}

/** 测试/迁移用：清空 spatial 静态缓存。 */
export function clearSpatialHashCache(): void {
  staticHashByConfig.clear();
}

/** 计算世界状态的确定性哈希（spatial 走静态缓存 + 动态增量）。 */
export function hashWorldState(state: WorldState): string {
  const { spatial, ...rest } = state;
  const restCanonical = canonicalize(rest);
  const spatialHash = hashSpatial(spatial).hash;
  return fnv1a32(restCanonical + '|spatial:' + spatialHash);
}
